import React, { useMemo, useRef } from "react";

import ProgressBar from "veneer/ProgressBar";
import TextInput from "veneer/TextInput";

export type PasswordValue = {
  password: string;
  retype?: string;
};

export type PasswordChangeMeta = {
  score: number; // 0..4
  valid: boolean;
};

export type PasswordInputProps = {
  autoComplete?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  helperText?: string;
  minLength?: number;
  onChange: (next: PasswordValue, meta: PasswordChangeMeta) => void;
  passwordLabel?: string;
  retypeLabel?: string;
  scoreToPercent?: (score: number) => number;
  showStrength?: boolean;
  size?: "small" | "medium";
  value: PasswordValue;
  withRetype?: boolean;
};

function computeValid(
  password: string,
  retype: string | undefined,
  minLength: number,
  withRetype: boolean,
): boolean {
  const hasInput = password.length > 0;
  const okLength = password.length >= minLength;
  if (!hasInput || !okLength) return false;
  if (!withRetype) return true;
  return password === (retype ?? "");
}

function getPasswordScore(password: string): number {
  if (!password) return 0;

  const hasLower = /[a-z]/.test(password);
  const hasUpper = /[A-Z]/.test(password);
  const hasDigit = /\d/.test(password);
  const hasSymbol = /[^a-zA-Z0-9]/.test(password);

  const varietyCount = [hasLower, hasUpper, hasDigit, hasSymbol].filter(
    Boolean,
  ).length;

  // Map variety count (0..4) -> base (0..3)
  // 0-1 => 0, 2 => 1, 3 => 2, 4 => 3
  let score = 0;
  if (varietyCount === 2) score = 1;
  if (varietyCount === 3) score = 2;
  if (varietyCount === 4) score = 3;

  // Length bonus
  if (password.length >= 12) score += 1;

  return Math.min(4, score);
}

function scoreToProgressType(score: number): "success" | "warning" | "error" {
  if (score === 4) return "success";
  if (score >= 2) return "warning";
  return "error";
}

function scoreLabel(score: number): string {
  switch (score) {
    case 0:
      return "Very weak";
    case 1:
      return "Weak";
    case 2:
      return "Fair";
    case 3:
      return "Strong";
    case 4:
      return "Very strong";
    default:
      return "Password strength";
  }
}

export function PasswordInput({
  autoComplete = "new-password",
  disabled = false,
  fullWidth = true,
  helperText = "A strong password combines letters, numbers and symbol characters.",
  minLength = 8,
  onChange,
  passwordLabel = "Password",
  retypeLabel = "Confirm Password",
  scoreToPercent = (s) => s * 20 + 20,
  showStrength = true,
  size = "medium",
  value,
  withRetype = false,
}: PasswordInputProps) {
  const password = value.password ?? "";
  const retype = value.retype ?? "";

  const score = useMemo(() => getPasswordScore(password), [password]);

  const valid = useMemo(
    () => computeValid(password, retype, minLength, withRetype),
    [password, retype, minLength, withRetype],
  );

  const showScore = showStrength && password.length > 0;

  const percent = useMemo(() => scoreToPercent(score), [score, scoreToPercent]);
  const progressType = useMemo(() => scoreToProgressType(score), [score]);

  const metaRef = useRef<PasswordChangeMeta>({ score, valid });
  metaRef.current = { score, valid };

  const emit = (nextPassword: string, nextRetype: string) => {
    const nextScore = getPasswordScore(nextPassword);
    const nextValid = computeValid(
      nextPassword,
      nextRetype,
      minLength,
      withRetype,
    );

    onChange(
      { password: nextPassword, retype: withRetype ? nextRetype : undefined },
      { score: nextScore, valid: nextValid },
    );
  };

  return (
    <>
      <TextInput
        autoComplete={autoComplete}
        disabled={disabled}
        fullWidth={fullWidth}
        helperText={
          helperText
            ? `${helperText} Your password must be at least ${minLength} characters long.`
            : `Your password must be at least ${minLength} characters long.`
        }
        label={passwordLabel}
        onChange={(val, _event) => emit(val ?? "", retype)}
        size={size}
        type="password"
        value={password}
      />

      {showScore && (
        <ProgressBar
          percent={percent}
          size="large"
          tooltip={scoreLabel(score)}
          type={progressType}
        />
      )}

      {withRetype && (
        <TextInput
          autoComplete={autoComplete}
          disabled={disabled}
          fullWidth={fullWidth}
          label={retypeLabel}
          onChange={(val, _event) => emit(password, val ?? "")}
          size={size}
          type="password"
          value={retype}
        />
      )}
    </>
  );
}
